import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import dayjs, {Dayjs} from "dayjs";
import {FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {Dialog} from "../base/Dialog";
import {Frame} from "../base/Frame";
import {ExportFileFormatType} from "../../types/domain/exportFileFormatType";
import {ExportFileRangeType} from "../../types/domain/exportFileRangeType";
import {getResultsDownloads} from "../../services/resultsService";
import {downloadBlob} from "../../utils/downloadUtils";
import {CustomDatePicker} from "../base/CustomDatePicker";
import {LabeledInput} from "../base/LabeledInput";
import {useFields} from "../../hooks/fieldsHooks";
import {useCompanyFarms} from "../../hooks/farmsHooks";
import {TextFieldFramed} from "../base/TextFieldFramed";
import {EXPORT_FILE_FORMAT_TYPES, EXPORT_FILE_RANGE_TYPES} from "../../utils/enumItemsUtils";
import {useLocalizedEnumItems} from "../../hooks/localizedEnumItemsHooks";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {useCompany} from "../../hooks/companiesHooks";
export type ModeType = "company" | "farm" | "field";

export interface Props {
    open: boolean;
    mode: ModeType;
    companyId: string;
    onClose: (isSuccessful: boolean) => void;
    farmIds?: string[];
    fieldId?: string;
}

export const DataDownloadDialog: React.FC<Props> = ({open, mode, onClose, companyId, farmIds = [], fieldId}) => {
    const {t} = useTranslation();
    const company = useCompany();
    const {dateFormat} = useUserPreferences();
    const [dateRange, setDateRange] = useState<ExportFileRangeType>(ExportFileRangeType.LATEST_DAY);
    const [fileFormat, setFileFormat] = useState<ExportFileFormatType>(ExportFileFormatType.CSV);
    const [periodStart, setPeriodStart] = useState<Dayjs | undefined>(undefined);
    const [periodEnd, setPeriodEnd] = useState<Dayjs | undefined>(undefined);
    const companyFarms = useCompanyFarms(companyId);
    const [name, setName] = useState(company?.data?.properties.name ?? "");
    const fieldsState = useFields({companyId, farmId: undefined, orderId: undefined});
    const [error, setError] = useState<string | undefined>(undefined);
    const {items: exportFileRangeTypes} = useLocalizedEnumItems(EXPORT_FILE_RANGE_TYPES);

    useEffect(() => {
        //Since farm names are equal for merged farms we can use the first one.
        if (mode === "farm" && companyFarms?.data?.mapByFarmId && farmIds[0]) {
            const farm = companyFarms.data.mapByFarmId[farmIds[0]];
            setName(farm ? farm.name : "");
        }
    }, [setName, mode, farmIds, companyFarms?.data?.mapByFarmId]);

    useEffect(() => {
        if (mode === "field" && fieldsState.items) {
            const field = fieldsState.items.find((item) => item.id === fieldId);
            setName(field ? field.name : "");
        }
    }, [fieldsState, setName, mode, fieldId]);

    useEffect(() => {
        if (dateRange === ExportFileRangeType.LATEST_DAY) {
            setPeriodStart(undefined);
            setPeriodEnd(undefined);
        } else if (dateRange === ExportFileRangeType.FULL_HISTORY) {
            setPeriodStart(undefined);
            setPeriodEnd(undefined);
        } else {
            const today = dayjs();
            setPeriodStart(today.subtract(5, "days"));
            setPeriodEnd(today);
        }
    }, [dateRange]);

    const onSubmit = () => {
        const queryParams = {
            companyId,
            farmIds,
            fieldId,
            periodStart: periodStart ? periodStart.format("YYYY-MM-DD") : periodStart,
            periodEnd: periodEnd ? periodEnd.format("YYYY-MM-DD") : periodEnd,
            format: fileFormat,
            dateFormat,
            latestDay: dateRange === ExportFileRangeType.LATEST_DAY ? true : undefined,
        };
        getResultsDownloads(queryParams)
            .then((data) => {
                downloadBlob(data, `data.${fileFormat}`);
            })
            .catch((e: Error) => {
                setError(e.message);
            });
    };

    return (
        <Dialog
            enableSubmit
            title={t(MAIN_TITLE_BY_MODE[mode])}
            titleIcon={<DownloadIcon color="primary" />}
            open={open}
            error={error}
            onClose={() => onClose(false)}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            submitTitle={t("dialogs.data_download.download_button")}
            onSubmit={onSubmit}
        >
            <>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <LabeledInput label={t(SECONDARY_TITLE_BY_MODE[mode])}>
                            <TextFieldFramed value={name}></TextFieldFramed>
                        </LabeledInput>
                    </Grid>
                    <Grid item xs={12}>
                        <Frame>
                            <Typography fontSize={16} textTransform="uppercase" sx={{marginBottom: "1rem"}}>
                                {t("dialogs.data_download.date_range.title")}
                            </Typography>
                            <RadioGroup
                                value={dateRange}
                                onChange={(event) => {
                                    setDateRange(event.target.value as ExportFileRangeType);
                                }}
                            >
                                {exportFileRangeTypes.map(({id, label}) => (
                                    <FormControlLabel
                                        key={id}
                                        value={id}
                                        control={<Radio sx={{paddingY: "0.3rem"}} />}
                                        label={label}
                                    />
                                ))}
                            </RadioGroup>

                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={
                                    dateRange !== ExportFileRangeType.SPECIFIC_RANGE
                                        ? VISUALLY_HIDDEN
                                        : {marginLeft: "1.8rem"}
                                }
                            >
                                <CustomDatePicker
                                    date={periodStart ? periodStart : null}
                                    onDateChange={(value) => {
                                        if (value) {
                                            setPeriodStart(value);
                                            if (!value.isBefore(periodEnd)) {
                                                setPeriodEnd(value.add(1, "day"));
                                            }
                                        } else {
                                            setPeriodStart(undefined);
                                        }
                                    }}
                                    sx={{width: "100%"}}
                                    disabled={dateRange !== ExportFileRangeType.SPECIFIC_RANGE}
                                />
                                <Typography variant="body1" sx={{mx: 1}}>
                                    –
                                </Typography>
                                <CustomDatePicker
                                    date={periodEnd ? periodEnd : null}
                                    onDateChange={(value) => {
                                        if (value) {
                                            setPeriodEnd(value);
                                        } else {
                                            setPeriodEnd(undefined);
                                        }
                                    }}
                                    sx={{width: "100%"}}
                                    disabled={dateRange !== ExportFileRangeType.SPECIFIC_RANGE}
                                />
                            </Stack>
                        </Frame>
                    </Grid>
                    <Grid item xs={12}>
                        <Frame>
                            <Typography fontSize={16} textTransform="uppercase" sx={{marginBottom: "1rem"}}>
                                {t("dialogs.data_download.file_format.title")}
                            </Typography>
                            <RadioGroup
                                value={fileFormat}
                                onChange={(event) => {
                                    setFileFormat(event.target.value as ExportFileFormatType);
                                }}
                            >
                                {EXPORT_FILE_FORMAT_TYPES.map(({id, label}) => (
                                    <FormControlLabel
                                        key={id}
                                        value={id}
                                        control={<Radio sx={{paddingY: "0.3rem"}} />}
                                        label={label}
                                    />
                                ))}
                            </RadioGroup>
                        </Frame>
                    </Grid>
                </Grid>
            </>
        </Dialog>
    );
};

const VISUALLY_HIDDEN = {
    opacity: 0,
};

const MAIN_TITLE_BY_MODE: Record<ModeType, string> = {
    company: "dialogs.data_download.title_company",
    farm: "dialogs.data_download.title_farm",
    field: "dialogs.data_download.title_field",
};

const SECONDARY_TITLE_BY_MODE: Record<ModeType, string> = {
    company: "dialogs.data_download.secondary_title_company",
    farm: "dialogs.data_download.secondary_title_farm",
    field: "dialogs.data_download.secondary_title_field",
};
